import { styled } from '@linaria/react';
import type { ReactElement, ReactNode } from 'react';
import { Icon } from '../icon/icon';

/** Semantic colour treatment of the badge. */
type BadgeTone = 'success' | 'neutral' | 'info' | 'warning' | 'danger';

/** Badge height — "sm" is 22px, "md" is 26px. */
type BadgeSize = 'sm' | 'md';

interface BadgeProps {
  /** Semantic colour treatment. */
  tone: BadgeTone;
  /** Optional ui-library `Icon` (Material Icons) name, rendered at 14px before the text. */
  icon?: string;
  /** Badge text. */
  children: ReactNode;
  /** Badge height. Defaults to "sm". */
  size?: BadgeSize;
  /** Native tooltip text. */
  title?: string;
  /** Pass-through `data-*` attributes (e.g. `data-bf`). */
  [dataAttribute: `data-${string}`]: string | undefined;
}

const ICON_SIZE = 14;

/**
 * Icon colour per tone. Passed to `Icon` as a prop because `Icon` paints its
 * colour inline, which a stylesheet rule could not override.
 */
const ICON_COLOR: Record<BadgeTone, string> = {
  success: 'var(--primary-darkest)',
  neutral: 'var(--ink-base)',
  info: 'var(--secondary-darkest)',
  warning: 'var(--ink-darkest)',
  danger: 'var(--red-darkest)',
};

/**
 * Render a compact status pill. Each tone pairs a light background with a dark
 * text colour that meets WCAG AA (4.5:1) contrast; the danger tone keeps its
 * text ink and carries the red only on the icon for the same reason.
 *
 * @param {BadgeProps} props - Tone, optional icon, size, text, and pass-through title/data attributes
 * @returns {ReactElement} The badge `<span>`
 * @example
 * <Badge tone="success" icon="check_circle">Connected</Badge>
 */
export function Badge({ tone, icon, children, size = 'sm', ...rest }: BadgeProps): ReactElement {
  return (
    <StyledBadge {...rest} data-tone={tone} data-size={size}>
      {icon && (
        // The Material icon renders its ligature name as text; hide it so it never pollutes the badge's text.
        <IconSlot aria-hidden>
          <Icon name={icon} size={ICON_SIZE} color={ICON_COLOR[tone]} />
        </IconSlot>
      )}
      {children}
    </StyledBadge>
  );
}

const StyledBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  box-sizing: border-box;
  border-radius: var(--border-radius-xlarge);
  font-size: var(--font-size-xsmall);
  font-weight: var(--font-weight-semibold);
  line-height: 1;
  white-space: nowrap;

  &[data-size='sm'] {
    height: 22px;
    padding: 0 8px;
  }

  &[data-size='md'] {
    height: 26px;
    padding: 0 10px;
  }

  &[data-tone='success'] {
    background: var(--primary-lightest);
    color: var(--primary-darkest);
  }

  &[data-tone='neutral'] {
    background: var(--sky-lighter);
    color: var(--ink-base);
  }

  &[data-tone='info'] {
    background: var(--secondary-lightest);
    color: var(--secondary-darkest);
  }

  &[data-tone='warning'] {
    background: var(--yellow-lightest);
    color: var(--ink-darkest);
  }

  &[data-tone='danger'] {
    background: var(--red-lightest);
    color: var(--ink-darkest);
  }
`;

const IconSlot = styled.span`
  display: inline-flex;
  line-height: 0;
`;

export type { BadgeProps, BadgeSize, BadgeTone };
